import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
  checkUberHealthServiceAvailability,
  resetUberHealthServiceAvailability,
} from '../store/rides';

const isAddressReadyForUberCheck = (address) =>
  Boolean(
    address?.coordinates?.lat != null &&
      address?.coordinates?.lng != null &&
      (address?.formattedAddress || address?.placeId)
  );

/**
 * Runs Uber Health trip estimates when pickup and destination have coordinates.
 * Create Ride step 3 and Quick Schedule (ScheduleRidePanel) both use this hook.
 * Does not set rides.loading — only uberHealthRouteAvailable / uberHealthRouteCheckPending.
 */
export const useUberHealthServiceAvailability = (currentValues, allowCheck) => {
  const dispatch = useDispatch();

  const pickupOk = useMemo(
    () => isAddressReadyForUberCheck(currentValues?.pickupAddressDetails),
    [
      currentValues?.pickupAddressDetails?.coordinates?.lat,
      currentValues?.pickupAddressDetails?.coordinates?.lng,
      currentValues?.pickupAddressDetails?.formattedAddress,
      currentValues?.pickupAddressDetails?.placeId,
    ]
  );

  const destOk = useMemo(
    () => isAddressReadyForUberCheck(currentValues?.destinationAddress),
    [
      currentValues?.destinationAddress?.coordinates?.lat,
      currentValues?.destinationAddress?.coordinates?.lng,
      currentValues?.destinationAddress?.formattedAddress,
      currentValues?.destinationAddress?.placeId,
    ]
  );

  useEffect(() => {
    return () => {
      dispatch(resetUberHealthServiceAvailability());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!allowCheck) {
      dispatch(resetUberHealthServiceAvailability());
      return;
    }

    if (!pickupOk || !destOk || !currentValues) {
      dispatch(resetUberHealthServiceAvailability());
      return;
    }

    const pickup = currentValues.pickupAddressDetails.coordinates;
    const dest = currentValues.destinationAddress.coordinates;

    dispatch(
      checkUberHealthServiceAvailability({
        pickupLat: pickup.lat,
        pickupLng: pickup.lng,
        dropoffLat: dest.lat,
        dropoffLng: dest.lng,
        roundTrip: Boolean(currentValues.isRoundtrip),
      })
    );
  }, [
    allowCheck,
    pickupOk,
    destOk,
    currentValues?.pickupAddressDetails?.coordinates?.lat,
    currentValues?.pickupAddressDetails?.coordinates?.lng,
    currentValues?.destinationAddress?.coordinates?.lat,
    currentValues?.destinationAddress?.coordinates?.lng,
    currentValues?.isRoundtrip,
    dispatch,
  ]);
};
